import { graphql } from 'gatsby';
/**
 * These helpers aggregate data from products and format them for use in
 * ExhibitorsPage, ExhibitorPage, ProducersPage, and ProducerPage.
 */

/**
 * Formats object that is compatible with the BrandCertifications component.
 * @param {object} props
 * @param {function} props.extractIdsFn
 * @param {object} props.id
 * @param {object} props.event
 * @returns {Certifications}
 */

/**
 * @typedef {Object} Certifications
 * @property {boolean} isVegan
 * @property {boolean} isVegetarian
 * @property {boolean} isCertifiedOrganic
 * @property {boolean} isOrganic
 * @property {boolean} isBiodynamic
 * @property {boolean} isSustainable
 */
export function getCertificationsForId({ extractIdsFn, id, event }) {
  const {
    byIsBiodynamic: biodynamicProducerIds,
    byIsOrganic: organicProducerIds,
    byIsCertifiedOrganic: certifiedOrganicProducerIds,
    byIsVegetarian: vegetarianProducerIds,
    byIsVegan: veganProducerIds,
    byIsSustainable: sustainableProducerIds,
  } = extractIdsFn({
    event,
  });
  return {
    // Standard way to show so that it can be used in exhibitors page for certifications block
    isVegan: veganProducerIds.includes(id),
    isVegetarian: vegetarianProducerIds.includes(id),
    isCertifiedOrganic: certifiedOrganicProducerIds.includes(id),
    isOrganic: organicProducerIds.includes(id),
    isBiodynamic: biodynamicProducerIds.includes(id),
    isSustainable: sustainableProducerIds.includes(id),
  };
}

/**
 * Formats a characteristics object that is compatible with the ProductCharacteristics Filter.
 * @param {object} props
 * @param {function} props.extractIdsFn
 * @param {object} props.id
 * @param {object} props.event
 * @returns {Characteristics}
 */
/**
 * @typedef {Object} Characteristics
 * @property {String[]} characteristics - array of applicable characteristics
 */
export function getCharacteristicsForId({ extractIdsFn, id, event }) {
  const {
    byIsBiodynamic: biodynamicProducerIds,
    byIsOrganic: organicProducerIds,
    byIsCertifiedOrganic: certifiedOrganicProducerIds,
    byIsVegetarian: vegetarianProducerIds,
    byIsVegan: veganProducerIds,
    byIsSustainable: sustainableProducerIds,
  } = extractIdsFn({
    event,
  });
  return [
    veganProducerIds.includes(id) && 'VEGAN',
    vegetarianProducerIds.includes(id) && 'VEGETARIAN',
    (certifiedOrganicProducerIds.includes(id) ||
      organicProducerIds.includes(id)) &&
      'ORGANIC',
    biodynamicProducerIds.includes(id) && 'BIODYNAMIC',
    sustainableProducerIds.includes(id) && 'SUSTAINABLE',
    // (await isKosher(product, {}, context, info)) && 'KOSHER',
  ].filter(Boolean);
}

/**
 * Extract ids of producers that have at least one product that has a given characteristic.
 * @param {object} props
 * @param {object} props.event - Event object containing registered products details.
 * @returns {ExtractIdsForCharacteristicsOutput}
 */
export function extractProducerIdsForCharacteristics({ event }) {
  return [
    'byIsBiodynamic',
    'byIsOrganic',
    'byIsCertifiedOrganic',
    'byIsVegetarian',
    'byIsVegan',
    'byIsSustainable',
  ].reduce(
    (acc, characteristic) => {
      const exhibitorsWithCharacteristic =
        event?.registeredProducts?.[characteristic]?.find((group) => {
          return group.fieldValue === 'true';
        })?.byPartner || [];

      const producerIds = exhibitorsWithCharacteristic.map(
        (exhibitor) => exhibitor.nodes[0].producerId
      );
      acc[characteristic] = producerIds;
      return acc;
    },
    {
      byIsBiodynamic: [],
      byIsOrganic: [],
      byIsCertifiedOrganic: [],
      byIsVegetarian: [],
      byIsVegan: [],
      byIsSustainable: [],
    }
  );
}

/**
 * Extract ids of exhibitors that have at least one product that has a given characteristic.
 * @param {object} props
 * @param {object} props.event - Event object containing registered products details.
 * @returns {ExtractIdsForCharacteristicsOutput}
 */
export function extractPartnerIdsForCharacteristics({ event }) {
  return [
    'byIsBiodynamic',
    'byIsOrganic',
    'byIsCertifiedOrganic',
    'byIsVegetarian',
    'byIsVegan',
    'byIsSustainable',
  ].reduce(
    (acc, characteristic) => {
      const exhibitorsWithCharacteristic =
        event?.registeredProducts?.[characteristic]?.find((group) => {
          return group.fieldValue === 'true';
        })?.byPartner || [];

      const partnerIds = exhibitorsWithCharacteristic.map(
        (exhibitor) => exhibitor.nodes[0].partnerId
      );
      acc[characteristic] = partnerIds;
      return acc;
    },
    {
      byIsBiodynamic: [],
      byIsOrganic: [],
      byIsCertifiedOrganic: [],
      byIsVegetarian: [],
      byIsVegan: [],
      byIsSustainable: [],
    }
  );
}

/**
 * @typedef {Object} ExtractIdsForCharacteristicsOutput
 * @property {String[]} byIsBiodynamic
 * @property {String[]} byIsOrganic
 * @property {String[]} byIsCertifiedOrganic
 * @property {String[]} byIsVegetarian
 * @property {String[]} byIsVegan
 * @property {String[]} byIsSustainable
 */

export const fragment = graphql`
  # This is for characteristics - it can't be used as filter in the exhibitors list because it brings the api server down.
  # This should eventually implemented as a filter on SingleRegistration. Something like this:
  # SingleRegistraiton.registeredProducts(filter: {isOrganic: {eq: true}}])
  # This would be a lot more efficient than the current implementation, and simpler to understand. The pattern
  # should also be used for RegisteredBrands.
  fragment brandCertifications_RegisteredProductConnection on Bottlebooks_RegisteredProductConnection {
    byIsCertifiedOrganic: group(field: isCertifiedOrganic) {
      fieldValue
      byPartner: group(field: partnerName, first: 1) {
        nodes {
          partnerId
          producerId
        }
      }
    }
    # The directory shouldn't care if it is certified or just made with organic grapes
    byIsOrganic: group(field: isOrganic) {
      fieldValue
      byPartner: group(field: partnerName, first: 1) {
        nodes {
          partnerId
          producerId
        }
      }
    }
    byIsBiodynamic: group(field: isBiodynamic) {
      fieldValue
      byPartner: group(field: partnerName, first: 1) {
        nodes {
          partnerId
          producerId
        }
      }
    }
    byIsVegan: group(field: isVegan) {
      fieldValue
      byPartner: group(field: partnerName, first: 1) {
        nodes {
          partnerId
          producerId
        }
      }
    }
    byIsVegetarian: group(field: isVegetarian) {
      fieldValue
      byPartner: group(field: partnerName, first: 1) {
        nodes {
          partnerId
          producerId
        }
      }
    }
    byIsSustainable: group(field: isSustainable) {
      fieldValue
      byPartner: group(field: partnerName, first: 1) {
        nodes {
          partnerId
          producerId
        }
      }
    }
  }
`;
